import Alert from '@risk-smart/themed-cloudscape-components/alert';
import { useTranslation } from 'react-i18next';

import styles from './RelationalClickthroughBanner.module.scss';

// RSP-5383 — relational data source widgets cannot click through to a single
// register, so their click-through is disabled. When the user clicks an element
// that would have navigated (a chart segment or KPI number), surface a
// high-contrast banner explaining why nothing happened, instead of the (too
// subtle) RSP-5304 info popover.
//
// The trigger lives at the click-through seam itself — `UniversalChart`'s
// `onClickUrl` resolver, which the leaf widgets already call on a navigable
// click — rather than a DOM-event overlay sniffing every interaction. That
// keeps it firing only on a genuine click-through attempt: a relational table
// passes no `onClickUrl`, so its chrome (sorting, column resize/reorder,
// pagination, settings) never raises the banner and stays fully usable. The
// timer state lives in `useAutoDismissBanner`.

type Props = {
  /** Whether the banner is currently shown (owned by `useAutoDismissBanner`). */
  visible: boolean;
};

/**
 * Absolute banner anchored to the widget's top — overlays content without
 * pushing it (AC8) and stays put when inner content scrolls (AC9).
 * `pointer-events-none` lets a repeat click fall through to the chart so the
 * timer still resets (AC3). `role="alert"` + `aria-live="assertive"` announce
 * the message without moving focus (AC10). `print:hidden` keeps this transient
 * state out of dashboard exports, matching the dashboard's other ephemeral UI.
 */
export const RelationalClickthroughBanner = ({ visible }: Props) => {
  const { t } = useTranslation();

  if (!visible) {
    return null;
  }

  return (
    <div
      role={'alert'}
      aria-live={'assertive'}
      className={`pointer-events-none absolute inset-x-0 top-0 z-10 p-1 print:hidden ${styles.banner}`}
    >
      <Alert type={'error'}>{t('dashboard.relational_disabled_alert')}</Alert>
    </div>
  );
};
